import React from 'react';
import logger from '@eb/logger-bugsnag';

/**
 * functionalSafeComponent takes in 3 arguments
 *  1. the original component
 *  2. the fallback component (or null)
 *  3. logger (or null)
 *
 * The wrapper will log to the console if a logger is not provided, the
 * recommended logger to provide is @eb/logg-bugsnag
 */
export const functionalSafeComponent = (
    WrappedComponent: any,
    CustomErrorComponent: any = null,
    loggerService: any = logger.error,
) => {
    const name = WrappedComponent.name || WrappedComponent.displayName;
    const renderComponent = (passedProps: any, passedState: any) => {
        try {
            const { hasError } = passedState;
            return hasError
                ? RenderErrorComponent(CustomErrorComponent)
                : WrappedComponent(passedProps);
        } catch (err) {
            (loggerService || errorLogger)(err);
            return RenderErrorComponent(CustomErrorComponent);
        }
    };
    return renderClientSafeComponent(renderComponent, name, loggerService);
};

const renderClientSafeComponent = (
    renderComponent: any,
    componentName: string,
    loggerService: any,
): any => {
    return class ErrorBoundaryWrapper extends React.Component {
        static displayName = `ErrorBoundary(${componentName})`;
        constructor(props: any) {
            super(props);
            this.state = { hasError: false, error: null };
        }

        static getDerivedStateFromError(error: any) {
            return { hasError: true, error };
        }

        componentDidCatch(error: any) {
            (loggerService || errorLogger)(error);
        }

        render() {
            return renderComponent(this.props, this.state);
        }
    };
};

const RenderErrorComponent = (CustomErrorComponent: any) => {
    return <div>{CustomErrorComponent ? <CustomErrorComponent /> : null}</div>;
};

const errorLogger = ({ error, errorInfo }: any) =>
    console.error(error, errorInfo);
